/**
 * Refusal copy for `/stablecoins/invoices/new`.
 *
 * The rule this module exists to keep: **a code the page has no copy for is
 * still shown, verbatim, as a bounded string.** It is never dropped (the
 * operator would see a failure with no reason), never guessed at (a made-up
 * explanation for `AMOUNT_…` could be the wrong one, and the operator would act
 * on it), and never rendered as markup (a reason code is server-controlled
 * text; `dangerouslySetInnerHTML` on it would be an injection path).
 *
 * The registry below names only codes that the issuer contract and the design
 * spec's refusal list already name — the issue-time guards documented on
 * `issueInvoice`/`createInvoiceDraft` in `src/features/invoicing/api.ts`, the
 * transport codes those routes return, and the design spec's own
 * "Refusal Codes" section
 * (`product-design/specs/09-invoicing/stablecoin-invoicing-spec.md`). It is a
 * *copy* table, not a permission table: nothing here decides anything.
 */

import { NETWORK_ERROR_CODE, MALFORMED_RESPONSE_CODE } from "@/features/invoicing/api";
import { COPY } from "./copy";

export interface RefusalCopy {
  /** Short heading for the refusal panel. */
  title: string;
  /** What it means and what the operator can do about it. */
  explanation: string;
  /** False for a code this page has no registered copy for. */
  known: boolean;
}

/**
 * Codes the contract names, with operator-facing copy.
 *
 * Grouped by the surface that raises them, so it is obvious where a new code
 * belongs when the contract grows one.
 */
const REGISTRY: Record<string, Omit<RefusalCopy, "known">> = {
  /* ---- issue-time guards (issueInvoice, createInvoiceDraft) ---- */
  INVOICING_NOT_ENABLED: {
    title: "Invoicing is not enabled for this organisation",
    explanation:
      "Invoicing is switched off in organisation settings, so no invoice can be created or issued. An organisation admin enables it in invoicing settings once KYB is approved and a collection wallet is validated.",
  },
  KYB_NOT_APPROVED: {
    title: "The organisation's KYB is not approved",
    explanation:
      "Issuing needs an approved KYB record: the legal name shown to payers is read from it and cannot be entered here. It stays refused until the verification completes.",
  },
  COLLECTION_WALLET_MISSING: {
    title: "No collection wallet for this asset and network",
    explanation:
      "The issuer's own collection wallet — per network and asset — controls the deposit address an issued invoice would use. None is configured for this combination, so the invoice cannot be issued. Add and validate the wallet in invoicing settings; Alloy invoicing holds no keys.",
  },
  ASSET_DECIMALS_MISMATCH: {
    title: "The asset's decimals do not match the configured asset",
    explanation:
      "The configured asset carries a different number of decimals than the amount was built with. Nothing was issued; re-read invoicing settings and start the draft again.",
  },
  ASSET_NOT_ENABLED: {
    title: "That asset is not enabled",
    explanation: "The organisation has no collection wallet for the chosen asset, so an invoice cannot be denominated in it.",
  },
  DUE_DATE_PAST: {
    title: "The due date is in the past",
    explanation: "An invoice cannot be issued with a due date that has already passed. Choose a future date and save the draft again.",
  },
  PAYER_ARCHIVED: {
    title: "That payer is archived",
    explanation: "An archived payer cannot hold a new invoice. Choose a current payer, or add a new one.",
  },
  DOCUMENT_NOT_DRAFT: {
    title: "That document is no longer a draft",
    explanation: "Only a draft can be edited. An issued invoice is frozen; duplicate it from the invoice page instead of editing it here.",
  },
  DOCUMENT_STATUS_INVALID: {
    title: "That action does not apply in this document's state",
    explanation: "The invoice has moved on from the state this action needs. Reload the invoice to see where it is now.",
  },
  VERSION_CONFLICT: {
    title: "The draft changed since this page read it",
    explanation:
      "Someone or something else saved this draft first. Reload the invoice, review its current lines, and apply the change again — the page never overwrites a version it did not read.",
  },

  /* ---- amount guards ---- */
  AMOUNT_PRECISION: {
    title: "The amount does not land on a whole base unit",
    explanation:
      "Quantity times unit price has to be a whole base unit for the asset. Rounding is never applied, because a rounded total would differ from the ledger. Adjust the quantity or the unit price.",
  },

  /* ---- transport / request shape ---- */
  VALIDATION_FAILED: {
    title: "The request was rejected as invalid",
    explanation: "The service rejected a field in this request. The message above names it; nothing was written.",
  },
  IDEMPOTENCY_KEY_REUSED: {
    title: "This request key was already used with a different body",
    explanation:
      "The call was retried with the same idempotency key but a different body, so the service refused to guess which one was meant. Start the action again.",
  },
  NOT_FOUND: {
    title: "That record no longer exists",
    explanation: "The payer or invoice this page was working with is gone. Reload and choose again.",
  },
  FORBIDDEN: {
    title: "This session may not do that",
    explanation: "The gateway refused the request for this session's role and scopes. Nothing was changed.",
  },
  DEPENDENCY_UNAVAILABLE: {
    title: "A service this depends on is unavailable",
    explanation: "The request could not be completed because a downstream service is not answering. Try again shortly.",
  },

  /* ---- design spec "Refusal Codes" section ---- */
  PAYMENT_IN_FLIGHT: {
    title: "A payment is in flight",
    explanation: "The invoice cannot change while a payment is confirming. This clears when the payment reaches a final state.",
  },
  APPROVER_MUST_DIFFER_FROM_PROPOSER: {
    title: "The approver must differ from the proposer",
    explanation: "Four-eyes: the principal that raised a proposal can never approve it. A different approver has to decide.",
  },
  MACHINE_PRINCIPAL_NOT_AN_APPROVER: {
    title: "A machine principal cannot approve",
    explanation: "An API key can propose, never approve. This page renders no approve control at all, and the service refuses one regardless.",
  },
  /**
   * A step-up refusal is *shown*, never worked around: this page has no way to
   * satisfy or skip a step-up, and deliberately does not retry one silently.
   */
  AUTH_STEP_UP_REQUIRED: {
    title: "Step-up authentication is required",
    explanation:
      "This action needs a fresh step-up for the signed-in person. Complete it in the session that raised the request; this page cannot perform, bypass or retry it.",
  },
  DECLARATION_REQUIRED: {
    title: "The payer declaration is missing",
    explanation: "The payer's declaration is frozen when the deposit address is issued. It has to be supplied before this step.",
  },
  DECLARATION_FROZEN: {
    title: "The payer declaration is frozen",
    explanation: "The declaration was locked when the address was issued and can no longer be edited.",
  },
  RATE_LIMITED: {
    title: "Too many requests",
    explanation: "The service is rate-limiting this session. Wait a moment and try the action again.",
  },
  PAY_LINK_NOT_ACTIVE: {
    title: "That pay link is not active",
    explanation: "The link has expired, been rotated or been revoked, so it no longer accepts payment.",
  },
};

/** Codes that are the page's own transport failures rather than the service's. */
const TRANSPORT: Record<string, Omit<RefusalCopy, "known">> = {
  [NETWORK_ERROR_CODE]: {
    title: "The service could not be reached",
    explanation:
      "The request never reached the gateway — the connection dropped or the service did not answer. Nothing was written. Check the connection and try again.",
  },
  [MALFORMED_RESPONSE_CODE]: {
    title: "The response could not be read",
    explanation:
      "The service answered with something this page cannot read, so nothing here is shown as though it had succeeded. Try again, and report the support reference if it repeats.",
  },
};

/**
 * Codes whose family the contract leaves open. A code in one of these families
 * is shown under the family's heading *with the code itself still on screen*,
 * so the family copy never stands in for a code nobody has described yet.
 */
const FAMILIES: { prefix: string; title: string; explanation: string }[] = [
  {
    prefix: "AMOUNT_",
    title: "The amount was refused",
    explanation:
      "The service refused an amount on this invoice. The exact code is shown below; the form's own arithmetic also checks for a line that would have to be rounded, which is the usual cause.",
  },
];

/**
 * Copy for a reason code. An unregistered code comes back `known: false` with
 * the code itself as the heading — the caller renders that verbatim, as text.
 */
export function describeRefusal(code: string): RefusalCopy {
  const exact = REGISTRY[code] ?? TRANSPORT[code];
  if (exact) return { ...exact, known: true };
  const family = FAMILIES.find((entry) => code.startsWith(entry.prefix));
  if (family) return { title: family.title, explanation: family.explanation, known: false };
  return { title: COPY.refusalHeading, explanation: COPY.refusalUnknownNote, known: false };
}
